import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BlockchainStatusIndicator from '../../components/ui/BlockchainStatusIndicator';
import CriticalAlertsPanel from '../../components/ui/CriticalAlertsPanel';
import QuickActionLauncher from '../../components/ui/QuickActionLauncher';
import EquipmentInventoryTable from './components/EquipmentInventoryTable';
import CalibrationScheduleCalendar from './components/CalibrationScheduleCalendar';
import MaintenanceCostTracker from './components/MaintenanceCostTracker';
import EquipmentPerformanceAnalytics from './components/EquipmentPerformanceAnalytics';
import CalibrationDocumentationModal from './components/CalibrationDocumentationModal';
import MaintenanceRequestModal from './components/MaintenanceRequestModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const LaboratoryEquipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [isCalibrationModalOpen, setIsCalibrationModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('inventory');

  useEffect(() => {
    // Mock equipment data
    const mockEquipment = [
      {
        id: 'eq_001',
        deviceId: 'HPLC-001',
        name: 'High Performance Liquid Chromatograph',
        model: 'Agilent 1260 Infinity II',
        serialNumber: 'AG12601234',
        department: 'Analytical Chemistry',
        status: 'operational',
        priority: 'high',
        lastMaintenance: '2024-08-15',
        nextDueDate: '2024-11-15',
        location: 'Lab Room 101',
        manufacturer: 'Agilent Technologies'
      },
      {
        id: 'eq_002',
        deviceId: 'GC-MS-002',
        name: 'Gas Chromatography Mass Spectrometer',
        model: 'Shimadzu GCMS-QP2020 NX',
        serialNumber: 'SH20205678',
        department: 'Analytical Chemistry',
        status: 'calibration_due',
        priority: 'high',
        lastMaintenance: '2024-07-20',
        nextDueDate: '2024-10-20',
        location: 'Lab Room 102',
        manufacturer: 'Shimadzu'
      },
      {
        id: 'eq_003',
        deviceId: 'UV-VIS-003',
        name: 'UV-Visible Spectrophotometer',
        model: 'PerkinElmer Lambda 365',
        serialNumber: 'PE36591011',
        department: 'Quality Control',
        status: 'operational',
        priority: 'medium',
        lastMaintenance: '2024-09-01',
        nextDueDate: '2024-12-01',
        location: 'QC Lab 201',
        manufacturer: 'PerkinElmer'
      },
      {
        id: 'eq_004',
        deviceId: 'BAL-004',
        name: 'Analytical Balance',
        model: 'Mettler Toledo XPR26',
        serialNumber: 'MT26121314',
        department: 'Quality Control',
        status: 'maintenance',
        priority: 'medium',
        lastMaintenance: '2024-09-10',
        nextDueDate: '2024-12-10',
        location: 'QC Lab 202',
        manufacturer: 'Mettler Toledo'
      },
      {
        id: 'eq_005',
        deviceId: 'FTIR-005',
        name: 'Fourier Transform Infrared Spectrometer',
        model: 'Thermo Nicolet iS50',
        serialNumber: 'TH50151617',
        department: 'Research & Development',
        status: 'operational',
        priority: 'low',
        lastMaintenance: '2024-08-25',
        nextDueDate: '2024-11-25',
        location: 'R&D Lab 301',
        manufacturer: 'Thermo Fisher Scientific'
      },
      {
        id: 'eq_006',
        deviceId: 'DSC-006',
        name: 'Differential Scanning Calorimeter',
        model: 'TA Instruments DSC 2500',
        serialNumber: 'TA25181920',
        department: 'Research & Development',
        status: 'out_of_service',
        priority: 'high',
        lastMaintenance: '2024-06-15',
        nextDueDate: '2024-09-15',
        location: 'R&D Lab 302',
        manufacturer: 'TA Instruments'
      }
    ];

    // Mock maintenance records
    const mockMaintenanceRecords = [
      {
        id: 'mr_001',
        equipmentId: 'eq_001',
        date: '2024-08-15',
        type: 'preventive',
        cost: 1250.00,
        description: 'Routine maintenance and calibration'
      },
      {
        id: 'mr_002',
        equipmentId: 'eq_002',
        date: '2024-07-20',
        type: 'corrective',
        cost: 2100.00,
        description: 'Detector replacement and recalibration'
      },
      {
        id: 'mr_003',
        equipmentId: 'eq_003',
        date: '2024-09-01',
        type: 'calibration',
        cost: 450.00,
        description: 'Annual calibration service'
      },
      {
        id: 'mr_004',
        equipmentId: 'eq_004',
        date: '2024-09-10',
        type: 'preventive',
        cost: 320.00,
        description: 'Balance calibration and cleaning'
      },
      {
        id: 'mr_005',
        equipmentId: 'eq_005',
        date: '2024-08-25',
        type: 'preventive',
        cost: 680.00,
        description: 'Optical system alignment and calibration'
      },
      {
        id: 'mr_006',
        equipmentId: 'eq_006',
        date: '2024-06-15',
        type: 'corrective',
        cost: 3200.00,
        description: 'Furnace repair and sensor replacement'
      }
    ];

    // Mock performance data
    const mockPerformanceData = [];
    const equipmentIds = mockEquipment?.map(eq => eq?.id);
    
    for (let i = 0; i < 90; i++) {
      const date = new Date();
      date?.setDate(date?.getDate() - i);
      
      equipmentIds?.forEach(equipmentId => {
        mockPerformanceData?.push({
          equipmentId,
          timestamp: date?.toISOString(),
          uptime: 85 + Math.random() * 14, // 85-99%
          accuracy: 95 + Math.random() * 4.5, // 95-99.5%
          efficiency: 80 + Math.random() * 18, // 80-98%
          errorCount: Math.floor(Math.random() * 3)
        });
      });
    }

    setEquipment(mockEquipment);
    setMaintenanceRecords(mockMaintenanceRecords);
    setPerformanceData(mockPerformanceData);
  }, []);

  const handleCalibrate = (equipmentItem) => {
    setSelectedEquipment(equipmentItem);
    setIsCalibrationModalOpen(true);
  };

  const handleMaintenance = (equipmentItem) => {
    setSelectedEquipment(equipmentItem);
    setIsMaintenanceModalOpen(true);
  };

  const handleViewDetails = (equipmentItem) => {
    console.log('View equipment details:', equipmentItem);
    // Navigate to detailed equipment view
  };

  const handleScheduleCalibration = () => {
    console.log('Schedule new calibration');
    // Open scheduling modal
  };

  const handleSaveCalibration = async (calibrationRecord) => {
    console.log('Saving calibration record:', calibrationRecord);
    // Save to blockchain and update equipment status
    setEquipment(prev => prev?.map(eq => 
      eq?.id === calibrationRecord?.equipmentId 
        ? { ...eq, status: 'operational', nextDueDate: calibrationRecord?.nextDueDate }
        : eq
    ));
  };

  const handleSubmitMaintenanceRequest = async (maintenanceRequest) => {
    console.log('Submitting maintenance request:', maintenanceRequest);
    // Submit request and update status
  };

  const getEquipmentSummary = () => {
    const total = equipment?.length;
    const operational = equipment?.filter(eq => eq?.status === 'operational')?.length;
    const needsAttention = equipment?.filter(eq => 
      eq?.status === 'calibration_due' || eq?.status === 'maintenance' || eq?.status === 'out_of_service'
    )?.length;
    const overdue = equipment?.filter(eq => {
      const dueDate = new Date(eq.nextDueDate);
      return dueDate < new Date();
    })?.length;

    return { total, operational, needsAttention, overdue };
  };

  const summary = getEquipmentSummary();

  const tabs = [
    { id: 'inventory', label: 'Equipment Inventory', icon: 'List' },
    { id: 'calendar', label: 'Calibration Schedule', icon: 'Calendar' },
    { id: 'costs', label: 'Maintenance Costs', icon: 'Euro' },
    { id: 'analytics', label: 'Performance Analytics', icon: 'BarChart3' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Laboratory Equipment - IFET ChainLog</title>
        <meta name="description" content="Monitor equipment calibration status, maintenance schedules, and blockchain-logged equipment usage for pharmaceutical testing compliance" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Laboratory Equipment</h1>
              <p className="text-muted-foreground mt-2">
                Monitor equipment calibration status, maintenance schedules, and performance analytics
              </p>
            </div>
            
            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <BlockchainStatusIndicator />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAlertsOpen(true)}
                iconName="Bell"
                iconSize={16}
              >
                Alerts
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleScheduleCalibration}
                iconName="Plus"
                iconSize={16}
              >
                Schedule Calibration
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Equipment</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.total}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Operational</p>
                  <p className="text-3xl font-bold text-success">{summary?.operational}</p>
                  <p className="text-xs text-muted-foreground">
                    {((summary?.operational / summary?.total) * 100)?.toFixed(1)}% of total
                  </p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Needs Attention</p>
                  <p className="text-3xl font-bold text-warning">{summary?.needsAttention}</p>
                  <p className="text-xs text-muted-foreground">
                    Maintenance or calibration required
                  </p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue</p>
                  <p className="text-3xl font-bold text-error">{summary?.overdue}</p>
                  <p className="text-xs text-muted-foreground">
                    Past calibration due date
                  </p>
                </div>
                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-error" />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-border mb-8">
            <nav className="flex space-x-8">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {activeTab === 'inventory' && (
              <EquipmentInventoryTable
                equipment={equipment}
                onCalibrate={handleCalibrate}
                onMaintenance={handleMaintenance}
                onViewDetails={handleViewDetails}
              />
            )}

            {activeTab === 'calendar' && (
              <CalibrationScheduleCalendar
                equipment={equipment}
                onScheduleCalibration={handleScheduleCalibration}
              />
            )}

            {activeTab === 'costs' && (
              <MaintenanceCostTracker
                equipment={equipment}
                maintenanceRecords={maintenanceRecords}
              />
            )}

            {activeTab === 'analytics' && (
              <EquipmentPerformanceAnalytics
                equipment={equipment}
                performanceData={performanceData}
              />
            )}
          </div>
        </div>
      </main>
      {/* Modals */}
      <CalibrationDocumentationModal
        isOpen={isCalibrationModalOpen}
        onClose={() => setIsCalibrationModalOpen(false)}
        equipment={selectedEquipment}
        onSave={handleSaveCalibration}
      />
      <MaintenanceRequestModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => setIsMaintenanceModalOpen(false)}
        equipment={selectedEquipment}
        onSubmit={handleSubmitMaintenanceRequest}
      />
      <CriticalAlertsPanel
        isOpen={isAlertsOpen}
        onClose={() => setIsAlertsOpen(false)}
      />
      <QuickActionLauncher />
    </div>
  );
};

export default LaboratoryEquipment;